import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ChargePointSelectors from 'Stores/ChargePoint/Selectors'
import ChargePointActions from 'Stores/ChargePoint/Actions'
import Styles from './ConfigurationList.module.css'
import RequestState from 'Enums/RequestState'
import {
  ConfigurationListItem,
  ConfigurationListItemSkeleton,
  IFToastMessage,
} from 'Components'
import AuthSelectors from 'Stores/Auth/Selectors'
import InfinityEnums from 'Enums/InfinityEnums'
import { useTranslation } from 'react-i18next'
import { toaster } from 'rsuite'
import { Virtuoso } from 'react-virtuoso'
import { useOverlayScrollbars } from 'overlayscrollbars-react'
import 'overlayscrollbars/overlayscrollbars.css'
const formatChargePointConfigList = (chargePointConfigList, filter) => {
  return Object.keys(chargePointConfigList).filter((key) => {
    const lower = filter ? filter.toLowerCase() : ''
    return key.toLowerCase().includes(lower)
  })
}

const ConfigurationList = ({
  chargePointConfigList,
  chargePointConfigListRequestState,
  updateChargePointConfig,
  chargePoints,
  selectedIndex,
  filter,
  operatorUser,
}) => {
  const { t } = useTranslation()
  const [loadingConfigs, setLoadingConfigs] = useState(
    Array(Object.keys(chargePointConfigList).length).fill(false),
  )
  const [editingConfigs, setEditingConfigs] = useState(
    Array(Object.keys(chargePointConfigList).length).fill(false),
  )
  const [formattedChargePointConfigList, setFormattedChargePointConfigList] =
    useState(formatChargePointConfigList(chargePointConfigList, filter))
  const editingList = (index, flag) => {
    setEditingConfigs((prevState) => {
      prevState[index] = flag
      return [...prevState]
    })
  }
  const onSuccess = (index, status) => {
    setLoadingConfigs((prevState) => {
      prevState[index] = false
      return [...prevState]
    })
    setEditingConfigs((prevState) => {
      prevState[index] = false
      return [...prevState]
    })

    toaster.push(
      <IFToastMessage
        type="success"
        text={t('ConfigurationList.EditSuccess')}
      />,
      { placement: 'topEnd' },
    )
    if (status === InfinityEnums.ConfigurationStatus.REBOOT_REQUIRED) {
      toaster.push(
        <IFToastMessage
          type="info"
          text={t('ConfigurationList.RebootRequired')}
        />,
        { placement: 'topEnd' },
      )
    }
  }

  const onFail = (index) => {
    setLoadingConfigs((prevState) => {
      prevState[index] = false
      return [...prevState]
    })
    setEditingConfigs((prevState) => {
      prevState[index] = true
      return [...prevState]
    })
    toaster.push(
      <IFToastMessage type="error" text={t('ConfigurationList.EditFail')} />,
      { placement: 'topEnd' },
    )
  }
  useEffect(() => {
    setEditingConfigs(
      Array(Object.keys(chargePointConfigList).length).fill(false),
    )
    setLoadingConfigs(
      Array(Object.keys(chargePointConfigList).length).fill(false),
    )
  }, [chargePointConfigListRequestState])
  useEffect(() => {
    const results = Object.keys(chargePointConfigList).filter((key) => {
      const lower = filter ? filter.toLowerCase() : ''
      return key.toLowerCase().includes(lower)
    })
    setEditingConfigs(Array(results.length).fill(false))
    setLoadingConfigs(Array(results.length).fill(false))
  }, [filter])

  useEffect(() => {
    setFormattedChargePointConfigList(
      formatChargePointConfigList(chargePointConfigList, filter),
    )
  }, [chargePointConfigList, filter])

  const options = {
    scrollbars: {
      visibility: 'auto',
      autoHide: 'move',
      autoHideDelay: 1000,
    },
  }
  const rootRef = useRef(null)
  const [scroller, setScroller] = useState(null)
  const [initialize, osInstance] = useOverlayScrollbars({
    options,
    defer: true,
  })

  useEffect(() => {
    const { current: root } = rootRef
    if (scroller && root) {
      initialize({
        target: root,
        elements: {
          viewport: scroller,
        },
      })
    }
    return () => osInstance()?.destroy()
  }, [scroller, initialize, osInstance])

  return (
    <div className={Styles.ListContainer}>
      {chargePointConfigListRequestState === RequestState.LOADING ? (
        <div>
          {[...Array(11)].map((e, index) => {
            return (
              <ConfigurationListItemSkeleton
                key={index}
                className={Styles.item}
              />
            )
          })}
        </div>
      ) : (
        <div
          ref={rootRef}
          data-overlayscrollbars=""
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          <Virtuoso
            scrollerRef={setScroller}
            data={formattedChargePointConfigList}
            increaseViewportBy={480}
            itemContent={(i, key) => {
              const isQrKey = key.toLowerCase().includes('qr')
              return (
                <ConfigurationListItem
                  key={key + '_' + i}
                  className={Styles.item}
                  name={key}
                  value={String(chargePointConfigList[key].value)}
                  isLoading={loadingConfigs[i]}
                  isEditing={editingConfigs[i]}
                  type={chargePointConfigList[key].type}
                  stationId={chargePoints[selectedIndex]._id}
                  generateLinks={
                    isQrKey
                      ? chargePoints[selectedIndex].evses
                          .flatMap((evse) => evse.connectors)
                          .filter((connector) => connector.uid !== 0)
                          .sort((a, b) => a.uid - b.uid)
                          .map(
                            (connector) =>
                              `${t('ConfigurationListItem.GenerateLink')} ${
                                connector.uid
                              }`,
                          )
                      : undefined
                  }
                  readonly={chargePointConfigList[key].readonly}
                  description={chargePointConfigList[key].description}
                  editingList={(flag) => {
                    editingList(i, flag)
                  }}
                  onDoneClick={(newValue) => {
                    let newloadingConfigs = [...loadingConfigs]
                    newloadingConfigs[i] = true
                    setLoadingConfigs((prevState) => {
                      prevState[i] = true
                      return [...prevState]
                    })
                    updateChargePointConfig(
                      chargePoints[selectedIndex]._id,
                      key,
                      newValue,
                      (status) => {
                        onSuccess(i, status)
                      },
                      () => {
                        onFail(i)
                      },
                    )
                  }}
                  editPermission={operatorUser.permissions.includes(
                    InfinityEnums.OperatorUserPermission.CAN_EDIT_CONFIGURATION,
                  )}
                />
              )
            }}
          />
        </div>
      )}
    </div>
  )
}
function mapDispatchToProps(dispatch) {
  return {
    updateChargePointConfig: (chargePointId, key, value, onSucess, onFail) =>
      dispatch(
        ChargePointActions.updateChargePointConfig(
          chargePointId,
          key,
          value,
          onSucess,
          onFail,
        ),
      ),
  }
}
const mapStateToProps = (state) => ({
  chargePointConfigList: ChargePointSelectors.getChargePointConfigs(state),
  chargePointConfigListRequestState:
    ChargePointSelectors.getFetchChargePointConfigListRequestState(state),
  updateChargePointConfigRequestState:
    ChargePointSelectors.getUpdateChargePointConfigRequestState(state),
  chargePoints: ChargePointSelectors.getChargePoints(state),
  selectedIndex: ChargePointSelectors.getSelectedIndex(state),
  operatorUser: AuthSelectors.getOperatorUser(state),
})

ConfigurationList.propTypes = {
  chargePointConfigList: PropTypes.object,
  chargePointConfigListRequestState: PropTypes.number,
  updateChargePointConfig: PropTypes.func,
  chargePoints: PropTypes.arrayOf(PropTypes.object),
  selectedIndex: PropTypes.number,
  filter: PropTypes.string,
  operatorUser: PropTypes.object,
}

export default connect(mapStateToProps, mapDispatchToProps)(ConfigurationList)
